import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    const form = e.target as HTMLFormElement;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value || '';
    if (honeypot.trim()) {
      setSubmitted(true);
      setEmail('');
      setTimeout(() => setSubmitted(false), 3000);
      return;
    }

    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value.toString());
    });

    try {
      await fetch('https://readdy.ai/api/form/d8ob8v880ubi47thme00', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
    } catch (_) {
      // silently handle
    }

    setSubmitted(true);
    setEmail('');
    setTimeout(() => setSubmitted(false), 3000);
  };

  return (
    <footer className="bg-primary-950 text-white">
      {/* ───── Main Columns ───── */}
      <div className="w-full px-6 md:px-10 py-16 md:py-20">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">

          {/* ───── Column 1: 品牌資訊 ───── */}
          <div className="lg:w-2/5">
            <Link to="/" className="inline-block">
              <img
                src="https://storage.readdy-site.link/project_files/d7679676-dd63-4051-b8b6-c3731369aef5/8c0d2f40-97f8-45c6-9a71-629ffe164689_compressed_logo-.webp"
                alt="山頂視角 PeakView"
                className="h-10 md:h-12 w-auto mb-4"
              />
            </Link>
            <p className="text-foreground-200 text-sm leading-relaxed max-w-md mb-1">
              山頂視角 PEAKVIEW
            </p>
            <p className="text-foreground-300 text-sm leading-relaxed max-w-md mb-1">
              商業理財、政治與以古諷今類人文社科書籍
            </p>
            <p className="text-foreground-400 text-sm leading-relaxed max-w-md mb-1">
              山頂視角文化事業有限公司
            </p>
            <p className="text-foreground-400 text-sm leading-relaxed max-w-md mb-3">
              官方網站：https://peakview.sonbook.net/
            </p>
            <p className="text-foreground-400 text-sm leading-relaxed max-w-md mb-6">
              本品牌委託崧博出版經銷，官方網站由崧博出版製作。
            </p>

            {/* Newsletter */}
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id="newsletter-form"
              className="flex gap-2 max-w-sm"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                readOnly
                aria-hidden="true"
                className="opacity-0 absolute top-0 left-0 h-0 w-0 -z-10 pointer-events-none"
              />
              <div className="relative flex-1">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="輸入電子郵件，訂閱最新出版訊息"
                  className="w-full px-4 py-3 text-sm bg-primary-900/60 border border-primary-800 rounded-lg text-white placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50 transition-colors"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-12 h-12 flex items-center justify-center bg-accent-500 hover:bg-accent-400 text-primary-950 rounded-lg transition-colors flex-shrink-0"
              >
                <i className="ri-mail-send-line text-lg"></i>
              </button>
            </form>
            {submitted && (
              <p className="text-accent-400 text-sm mt-2">感謝訂閱！最新出版訊息將寄送至您的信箱。</p>
            )}
          </div>

          {/* ───── Column 2: 網站導覽 ───── */}
          <div className="flex-1">
            <h3 className="text-sm font-label font-semibold text-white mb-4">網站導覽</h3>
            <div className="flex flex-col gap-2.5">
              <Link to="/" className="text-sm text-foreground-400 hover:text-white transition-colors whitespace-nowrap">
                首頁
              </Link>
              <Link to="/about" className="text-sm text-foreground-400 hover:text-white transition-colors whitespace-nowrap">
                品牌介紹
              </Link>
              <Link to="/stores" className="text-sm text-foreground-400 hover:text-white transition-colors whitespace-nowrap">
                合作通路
              </Link>
              <Link to="/faq" className="text-sm text-foreground-400 hover:text-white transition-colors whitespace-nowrap">
                常見問答
              </Link>
            </div>
          </div>

          {/* ───── Column 3: 相關網站 ───── */}
          <div className="flex-1">
            <h3 className="text-sm font-label font-semibold text-white mb-4">相關網站</h3>
            <div className="flex flex-col gap-4">
              <div>
                <a
                  href="https://sonbook.net/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-sm font-medium text-foreground-200 hover:text-white transition-colors whitespace-nowrap inline-flex items-center gap-1"
                >
                  崧博出版主站
                  <i className="ri-external-link-line text-xs"></i>
                </a>
                <p className="text-xs text-foreground-500 mt-0.5 leading-relaxed">
                  崧博出版資訊、出版服務與委託經銷品牌介紹。
                </p>
              </div>
              <div>
                <a
                  href="https://catalog.sonbook.net/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-sm font-medium text-foreground-200 hover:text-white transition-colors whitespace-nowrap inline-flex items-center gap-1"
                >
                  崧博書籍資料庫
                  <i className="ri-external-link-line text-xs"></i>
                </a>
                <p className="text-xs text-foreground-500 mt-0.5 leading-relaxed">
                  完整書名、作者、ISBN、內容主題及相關出版資訊查詢。
                </p>
              </div>
              <div>
                <a
                  href="https://sonbook.net/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-sm font-medium text-foreground-200 hover:text-white transition-colors whitespace-nowrap inline-flex items-center gap-1"
                >
                  查看崧博出版與其他經銷品牌
                  <i className="ri-external-link-line text-xs"></i>
                </a>
              </div>
            </div>
          </div>

        </div>

        {/* ───── 內容責任說明 ───── */}
        <div className="mt-14 pt-10 border-t border-primary-800/60">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-12">
            <div className="flex-1">
              <h3 className="text-sm font-label font-semibold text-white mb-3">書籍資訊說明</h3>
              <p className="text-xs text-foreground-400 leading-relaxed">
                完整書名、作者、ISBN、內容主題、出版品牌及相關出版資訊，以崧博書籍資料庫中的個別書籍頁為準。
              </p>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-label font-semibold text-white mb-3">內容責任</h3>
              <p className="text-xs text-foreground-400 leading-relaxed">
                個別作品內容與作者觀點，不代表山頂視角或崧博出版的官方立場。網站所呈現內容為出版與一般知識資訊，不能取代投資、財務、稅務或法律專業意見。
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ───── Bottom Bar ───── */}
      <div className="border-t border-primary-800/60">
        <div className="w-full px-6 md:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} 山頂視角文化事業有限公司
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="w-9 h-9 flex items-center justify-center border border-primary-700 rounded-lg text-foreground-400 hover:text-white hover:border-primary-500 transition-colors">
              <i className="ri-facebook-line text-sm"></i>
            </a>
            <a href="#" className="w-9 h-9 flex items-center justify-center border border-primary-700 rounded-lg text-foreground-400 hover:text-white hover:border-primary-500 transition-colors">
              <i className="ri-twitter-x-line text-sm"></i>
            </a>
            <a href="#" className="w-9 h-9 flex items-center justify-center border border-primary-700 rounded-lg text-foreground-400 hover:text-white hover:border-primary-500 transition-colors">
              <i className="ri-instagram-line text-sm"></i>
            </a>
            <a href="#" className="w-9 h-9 flex items-center justify-center border border-primary-700 rounded-lg text-foreground-400 hover:text-white hover:border-primary-500 transition-colors">
              <i className="ri-youtube-line text-sm"></i>
            </a>
          </div>
          <span className="text-xs text-foreground-500 whitespace-nowrap">隱私政策</span>
        </div>
      </div>
    </footer>
  );
}